import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase/config';
import {
    collection, getDocs, query, orderBy,
    doc, updateDoc, onSnapshot, where, limit
} from 'firebase/firestore';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { showError, showSuccess } from '../utils/notifications';

function TokenValidationSD() {
    const [tokens, setTokens] = useState([]);
    const [recharges, setRecharges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('token-validation');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingTokenId, setEditingTokenId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [showLegacy, setShowLegacy] = useState(false);
    const [syncing, setSyncing] = useState(false);

    // Cargar tokens de ticketRequestsInternas (en_uso=true)
    useEffect(() => {
        const tokensRef = collection(db, 'ticketRequestsInternas');
        const q = query(tokensRef, where('en_uso', '==', true));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const tokensList = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                const year = doc.id.startsWith('2026') || (data.fechaEcuador && data.fechaEcuador.startsWith('2026')) ? 2026 : 2025;
                tokensList.push({ id: doc.id, ...data, year });
            });
            tokensList.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
            setTokens(tokensList);
            setLoading(false);
        }, (error) => {
            console.error("Error al cargar tokens:", error);
            showError("Error", "No se pudieron cargar los tokens");
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Cargar todas las recargas para validación
    useEffect(() => {
        const fetchRecharges = async () => {
            try {
                // Solo cargar recargas del 2026 para validación por rendimiento y solicitud
                const rechargesRef = collection(db, 'RechargeInternas');
                // Intentamos filtrar por fecha para no traer miles de registros antiguos
                const startOf2026 = new Date('2026-01-01T00:00:00Z');
                const q = query(
                    rechargesRef,
                    where('date', '>=', startOf2026),
                    orderBy('date', 'desc')
                );
                const snapshot = await getDocs(q);
                const rechargesList = [];
                snapshot.forEach((doc) => {
                    rechargesList.push({ id: doc.id, ...doc.data() });
                });
                setRecharges(rechargesList);
            } catch (error) {
                console.error("Error al cargar recargas:", error);
                // Si falla el filtro (por falta de índice), intentamos cargar todo pero avisamos
                try {
                    const qBasic = query(collection(db, 'RechargeInternas'), limit(2000));
                    const snapshotBasic = await getDocs(qBasic);
                    const rechargesList = [];
                    snapshotBasic.forEach((doc) => {
                        rechargesList.push({ id: doc.id, ...doc.data() });
                    });
                    setRecharges(rechargesList);
                } catch (e) {
                    console.error("Error crítico cargando recargas:", e);
                }
            }
        };

        fetchRecharges();
    }, []);

    // Procesar datos para agrupar recargas por token
    const processedData = useMemo(() => {
        if (tokens.length === 0) return [];

        // Agrupar recargas por tokenticked
        const rechargesByToken = recharges.reduce((acc, recharge) => {
            const token = recharge.tokenticked;
            if (token) {
                if (!acc[token]) {
                    acc[token] = {
                        totalTickets: 0,
                        count: 0
                    };
                }
                acc[token].totalTickets += (recharge.ticketsAdded || 0);
                acc[token].count += 1;
            }
            return acc;
        }, {});

        return tokens.map(tokenDoc => {
            const isLegacy = tokenDoc.year < 2026;

            // Si es legacy (2025), no validamos contra recargas reales por solicitud
            if (isLegacy) {
                return {
                    ...tokenDoc,
                    calculatedAuto: tokenDoc.ticketsConsumidosAuto || 0,
                    calculatedMoto: tokenDoc.ticketsConsumidosMoto || 0,
                    rechargeCountAuto: 0,
                    rechargeCountMoto: 0,
                    diffAuto: 0,
                    diffMoto: 0,
                    status: 'legacy'
                };
            }

            const autoToken = tokenDoc.tokenAuto;
            const motoToken = tokenDoc.tokenMoto;

            const autoRecharges = rechargesByToken[autoToken] || { totalTickets: 0, count: 0 };
            const motoRecharges = rechargesByToken[motoToken] || { totalTickets: 0, count: 0 };

            return {
                ...tokenDoc,
                calculatedAuto: autoRecharges.totalTickets,
                calculatedMoto: motoRecharges.totalTickets,
                rechargeCountAuto: autoRecharges.count,
                rechargeCountMoto: motoRecharges.count,
                diffAuto: (tokenDoc.ticketsConsumidosAuto || 0) - autoRecharges.totalTickets,
                diffMoto: (tokenDoc.ticketsConsumidosMoto || 0) - motoRecharges.totalTickets,
                status: 'current'
            };
        });
    }, [tokens, recharges]);

    const filteredData = useMemo(() => {
        let data = processedData;

        // Filtrar por año si no se quiere ver legacy
        if (!showLegacy) {
            data = data.filter(item => item.year >= 2026);
        }

        if (!searchTerm) return data;
        const term = searchTerm.toLowerCase();
        return data.filter(item =>
            (item.tokenAuto && item.tokenAuto.toLowerCase().includes(term)) ||
            (item.tokenMoto && item.tokenMoto.toLowerCase().includes(term)) ||
            (item.uso && item.uso.toLowerCase().includes(term)) ||
            (item.id && item.id.toLowerCase().includes(term))
        );
    }, [processedData, searchTerm, showLegacy]);

    const handleUpdateUso = async (tokenId) => {
        try {
            const tokenRef = doc(db, 'ticketRequestsInternas', tokenId);
            await updateDoc(tokenRef, {
                notes: editValue
            });
            showSuccess("Actualizado", "El uso del token ha sido actualizado correctamente");
            setEditingTokenId(null);
        } catch (error) {
            console.error("Error al actualizar uso:", error);
            showError("Error", "No se pudo actualizar el uso del token");
        }
    };

    // Función para marcar tokens antiguos como completados (sincronizados al 100%)
    const handleSyncLegacyTokens = async () => {
        const legacyTokens = tokens.filter(t => t.year < 2026);
        if (legacyTokens.length === 0) {
            showSuccess("Info", "No hay tokens de 2025 para sincronizar");
            return;
        }

        if (!window.confirm(`¿Estás seguro de marcar ${legacyTokens.length} tokens de 2025 como COMPLETADOS e INVÁLIDOS? Esto sincronizará el consumo al 100% de su capacidad.`)) {
            return;
        }

        setSyncing(true);
        try {
            let count = 0;
            for (const token of legacyTokens) {
                const tokenRef = doc(db, 'ticketRequestsInternas', token.id);
                await updateDoc(tokenRef, {
                    ticketsConsumidosAuto: token.cantidadAuto || 0,
                    ticketsConsumidosMoto: token.cantidadMoto || 0,
                    status: 'completed',
                    isValid: false
                });
                count++;
            }
            showSuccess("Éxito", `${count} tokens de 2025 han sido marcados como completados`);
        } catch (error) {
            console.error("Error sincronizando tokens:", error);
            showError("Error", "Ocurrió un error al sincronizar los tokens");
        } finally {
            setSyncing(false);
        }
    };

    const startEditing = (token) => {
        setEditingTokenId(token.id);
        setEditValue(token.notes || token.uso || '');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen bg-gray-100 transition-all duration-300 ease-in-out">
            <MobileHeader
                title="Validación de Token SD"
                toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />

            <div className="flex flex-1 overflow-hidden">
                <Sidebar
                    activeSection={activeSection}
                    setActiveSection={setActiveSection}
                    isMobileMenuOpen={isMobileMenuOpen}
                    setIsMobileMenuOpen={setIsMobileMenuOpen}
                />

                <div className="flex-1 overflow-auto">
                    <header className="bg-white shadow px-6 py-4 hidden md:block">
                        <h1 className="text-xl font-bold text-gray-900">Validación de Token SD</h1>
                        <p className="text-sm text-gray-600">Administración y control de consumo de tokens</p>
                    </header>

                    <main className="p-6">
                        <div className="mb-6 bg-white p-4 rounded-lg shadow">
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                <div className="relative flex-1">
                                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Buscar por token, ID o uso..."
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="flex flex-wrap items-center gap-4">
                                    <label className="inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                            checked={showLegacy}
                                            onChange={(e) => setShowLegacy(e.target.checked)}
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Mostrar tokens 2025</span>
                                    </label>

                                    <button
                                        onClick={handleSyncLegacyTokens}
                                        disabled={syncing}
                                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${syncing ? 'bg-gray-400' : 'bg-amber-600 hover:bg-amber-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500`}
                                    >
                                        {syncing ? (
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                        )}
                                        Sincronizar Año 2025
                                    </button>

                                    <div className="text-sm text-gray-500">
                                        Mostrando: <span className="font-bold">{filteredData.length}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white shadow rounded-lg overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha / ID</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tokens (Auto / Moto)</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Consumo Auto</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Consumo Moto</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uso / Destino</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredData.map((token) => (
                                            <tr key={token.id} className={`${token.year < 2026 ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">{token.fechaEcuador || token.id}</div>
                                                            <div className="text-xs text-gray-500">{token.id}</div>
                                                        </div>
                                                        {token.year < 2026 && (
                                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-800">
                                                                2025
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-xs">
                                                        <span className="font-semibold text-blue-600">A:</span> {token.tokenAuto}
                                                    </div>
                                                    <div className="text-xs mt-1">
                                                        <span className="font-semibold text-green-600">M:</span> {token.tokenMoto}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm">
                                                        <span className="font-bold">{token.calculatedAuto}</span> / {token.ticketsConsumidosAuto || 0}
                                                    </div>
                                                    {token.year >= 2026 ? (
                                                        <div className={`text-xs ${token.diffAuto === 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                            {token.diffAuto === 0 ? '✓ Sincronizado' : `Dif: ${token.diffAuto}`}
                                                        </div>
                                                    ) : (
                                                        <div className="text-xs text-gray-500 italic">
                                                            No se valida (Legacy)
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm">
                                                        <span className="font-bold">{token.calculatedMoto}</span> / {token.ticketsConsumidosMoto || 0}
                                                    </div>
                                                    {token.year >= 2026 ? (
                                                        <div className={`text-xs ${token.diffMoto === 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                            {token.diffMoto === 0 ? '✓ Sincronizado' : `Dif: ${token.diffMoto}`}
                                                        </div>
                                                    ) : (
                                                        <div className="text-xs text-gray-500 italic">
                                                            No se valida (Legacy)
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    {editingTokenId === token.id ? (
                                                        <input
                                                            type="text"
                                                            className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <div className="text-sm text-gray-900 italic max-w-xs truncate">
                                                            {token.notes || token.uso || <span className="text-gray-400">Sin descripción</span>}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    {editingTokenId === token.id ? (
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleUpdateUso(token.id)}
                                                                className="text-blue-600 hover:text-blue-900 bg-blue-50 px-2 py-1 rounded"
                                                            >
                                                                Guardar
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingTokenId(null)}
                                                                className="text-gray-600 hover:text-gray-900 bg-gray-50 px-2 py-1 rounded"
                                                            >
                                                                Cancelar
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => startEditing(token)}
                                                            className="text-blue-600 hover:text-blue-900"
                                                        >
                                                            Editar Uso
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredData.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                                                    No se encontraron tokens que coincidan con la búsqueda.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}

export default TokenValidationSD;
